import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { setOwners } from '@/features/elements';
import type { OwnerRef } from '@/features/elements';

import * as ownersApi from '../api/ownersApi';
import type { OwnerlessPage, OwnerlessSummary } from '../model/types';

export const ownerlessKeys = {
  all: ['ownerless'] as const,
  summary: (runId: number | null) => ['ownerless', 'summary', runId] as const,
  branch: (source: number | null, runId: number | null, reviewed: boolean, offset: number) =>
    ['ownerless', 'branch', source, runId, reviewed, offset] as const,
};

export function useOwnerless(importRunId: number | null) {
  return useQuery<OwnerlessSummary>({
    queryKey: ownerlessKeys.summary(importRunId),
    queryFn: ({ signal }) => ownersApi.fetchOwnerless(importRunId, signal),
  });
}

export function useOwnerlessBranch(
  sourceElementId: number | null,
  importRunId: number | null,
  includeReviewed: boolean,
  offset: number,
  enabled: boolean,
) {
  return useQuery<OwnerlessPage>({
    queryKey: ownerlessKeys.branch(sourceElementId, importRunId, includeReviewed, offset),
    queryFn: ({ signal }) =>
      ownersApi.fetchOwnerlessBranch(
        sourceElementId,
        { importRunId, includeReviewed, offset, limit: 50 },
        signal,
      ),
    enabled,
  });
}

/**
 * „Przyjmuję dziedziczenie" — **optimistic**.
 *
 * ⭐ To jest akcja, którą klika się kilkadziesiąt razy z rzędu, i jedyna sensowna reakcja to
 * natychmiastowe zniknięcie wiersza z listy. Czekanie na serwer zamieniłoby pracę seryjną
 * w klikanie z przerwami.
 *
 * ⚠ Migawka obejmuje **całe zapytanie**, nie sam wiersz: cofnięcie musi przywrócić też
 * `total`, inaczej po nieudanym zapisie licznik i lista mówiłyby dwie różne rzeczy.
 */
export function useAcceptInherited(
  sourceElementId: number | null,
  importRunId: number | null,
  includeReviewed: boolean,
  offset: number,
) {
  const queryClient = useQueryClient();
  const key = ownerlessKeys.branch(sourceElementId, importRunId, includeReviewed, offset);

  return useMutation({
    mutationFn: ownersApi.acceptInherited,
    onMutate: async (input: { elementIds?: readonly number[]; branchRootId?: number }) => {
      await queryClient.cancelQueries({ queryKey: key });

      const snapshot = queryClient.getQueryData<OwnerlessPage>(key);

      if (snapshot && input.elementIds && !includeReviewed) {
        const removed = new Set(input.elementIds);

        queryClient.setQueryData<OwnerlessPage>(key, {
          ...snapshot,
          items: snapshot.items.filter((item) => !removed.has(item.id)),
          total: Math.max(0, snapshot.total - removed.size),
        });
      }

      return { snapshot };
    },
    onError: (_error, _input, context) => {
      if (context?.snapshot) queryClient.setQueryData(key, context.snapshot);
    },
    onSettled: () => {
      // Podsumowanie kubełków też się zmienia — licznik „obejrzane" jest jego częścią.
      void queryClient.invalidateQueries({ queryKey: ownerlessKeys.all });
    },
  });
}

/**
 * Nadanie właściciela **z listy roboczej** — optimistic, z wyrzuceniem wiersza.
 *
 * ⭐ Osobny hook, choć zapis robi ten sam endpoint co ekran elementu: to tutaj wiadomo, że
 * element **przestaje należeć do listy**, i tutaj trzeba unieważnić jej podsumowanie.
 * ⚠ Ogólny `useSetOwners` unieważnia własność i detal elementu — o istnieniu tej listy
 * nie wie i wiedzieć nie powinien. Bez tego licznik kubełka **zostawał nieaktualny**
 * (widziane na ekranie 2026-08-08: serwer mówił 5, ekran dalej 6).
 */
export function useAssignOwner(
  sourceElementId: number | null,
  importRunId: number | null,
  includeReviewed: boolean,
  offset: number,
) {
  const queryClient = useQueryClient();
  const key = ownerlessKeys.branch(sourceElementId, importRunId, includeReviewed, offset);

  return useMutation({
    mutationFn: ({ elementId, owner }: { elementId: number; owner: OwnerRef }) =>
      setOwners({ elementId, owners: [owner], inherit: false }),

    onMutate: async ({ elementId }) => {
      await queryClient.cancelQueries({ queryKey: key });

      const snapshot = queryClient.getQueryData<OwnerlessPage>(key);

      if (snapshot) {
        queryClient.setQueryData<OwnerlessPage>(key, {
          ...snapshot,
          items: snapshot.items.filter((item) => item.id !== elementId),
          total: Math.max(0, snapshot.total - 1),
        });
      }

      return { snapshot };
    },

    onError: (_error, _input, context) => {
      if (context?.snapshot) queryClient.setQueryData(key, context.snapshot);
    },

    onSettled: () => void queryClient.invalidateQueries({ queryKey: ownerlessKeys.all }),
  });
}
